import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formatCurrency } from '../../utils/formatters';
import { Sparkles, Target, Calendar, DollarSign, ChevronRight, ChevronLeft, Edit, Lightbulb } from 'lucide-react';

interface CreateCustomDreamProps {
  onSave: (dreamData: { 
    name: string; 
    description: string; 
    monthlyGoal: number; 
    timeframe: number;
  }) => void;
  onCancel: () => void;
}

const CreateCustomDream: React.FC<CreateCustomDreamProps> = ({ onSave, onCancel }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [monthlyGoal, setMonthlyGoal] = useState(50000);
  const [timeframe, setTimeframe] = useState(15);
  
  // Presets for monthly goals
  const monthlyGoalPresets = [
    { label: 'Básico', value: 30000 },
    { label: 'Estándar', value: 50000 },
    { label: 'Premium', value: 100000 }
  ];
  
  // Presets for timeframes
  const timeframePresets = [
    { label: 'Corto', value: 5 },
    { label: 'Medio', value: 15 },
    { label: 'Largo', value: 25 }
  ];
  
  const handleSave = () => {
    // Basic validation
    if (!name.trim()) {
      alert('Por favor ingresa un nombre para tu sueño');
      return;
    }
    
    if (!description.trim()) {
      alert('Por favor ingresa una descripción para tu sueño');
      return;
    }
    
    if (monthlyGoal <= 0) {
      alert('La meta mensual debe ser mayor a 0');
      return;
    }
    
    if (timeframe < 5 || timeframe > 30) {
      alert('El plazo debe estar entre 5 y 30 años');
      return;
    }
    
    onSave({
      name,
      description,
      monthlyGoal,
      timeframe
    });
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Edit className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Crear Sueño Personalizado</h2>
              <p className="text-indigo-100">Define tu propio sueño y descubre cómo hacerlo realidad con Cosecha Capital</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" />
              <span>Detalles de tu Sueño</span>
            </h3>
            
            {/* Dream Name */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Nombre de tu Sueño
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Mi Casa en la Playa"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {/* Dream Description */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Descripción
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe tu sueño y por qué es importante para ti..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            
            {/* Monthly Goal */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Meta de Ingreso Mensual
              </label>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                {monthlyGoalPresets.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => setMonthlyGoal(preset.value)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      monthlyGoal === preset.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <p className="font-medium mb-1">{preset.label}</p>
                      <p className="text-lg font-bold">
                        {formatCurrency(preset.value, 'MXN')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">mensual</p>
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={monthlyGoal}
                  onChange={(e) => setMonthlyGoal(Math.max(0, parseInt(e.target.value) || 0))}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Ingresa tu meta mensual"
                />
                <span className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                  MXN
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Ingresa el monto mensual que deseas recibir al final del plazo
              </p>
            </div>
            
            {/* Timeframe */}
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                Plazo de Inversión
              </label>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                {timeframePresets.map((preset, index) => (
                  <button
                    key={index}
                    onClick={() => setTimeframe(preset.value)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      timeframe === preset.value
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <p className="font-medium mb-1">{preset.label}</p>
                      <p className="text-lg font-bold">{preset.value}</p>
                      <p className="text-xs text-gray-500 mt-1">años</p>
                    </div>
                  </button>
                ))}
              </div>
              
              <div className="flex gap-2">
                <input
                  type="number"
                  min="5"
                  max="30"
                  value={timeframe}
                  onChange={(e) => setTimeframe(Math.max(5, Math.min(30, parseInt(e.target.value) || 5)))}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Años (5-30)"
                />
                <span className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                  años
                </span>
              </div>
              <p className="text-xs text-gray-500">
                Ingresa el número de años en que deseas alcanzar tu meta (entre 5 y 30)
              </p>
            </div>
          </div>
          
          {/* Tips Section */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-indigo-600" />
              </div>
              <h4 className="font-medium text-indigo-800">Consejos para definir tu sueño</h4>
            </div>
            
            <ul className="space-y-2 text-sm text-indigo-700">
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-1">•</span>
                <span>Sé específico con tu meta mensual para obtener resultados más precisos</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-1">•</span>
                <span>Considera un plazo realista que te permita alcanzar tu meta</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-1">•</span>
                <span>Recuerda que los primeros 5 años son de maduración de la inversión</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-500 mt-1">•</span>
                <span>A mayor plazo, mayor será el efecto del interés compuesto</span>
              </li>
            </ul>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onCancel}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Volver</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleSave}
              className="px-8 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Crear Sueño</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CreateCustomDream;